import styles from './MarketCapacity.module.scss';

import * as React from 'react';
import _ from 'lodash';
import moment from 'moment';

import { useSelector } from 'react-redux';
import {
  ActivityItem,
  CheckboxVisibility,
  ConstrainMode,
  DefaultButton,
  DetailsList,
  DetailsListLayoutMode,
  Dropdown,
  IColumn,
  Icon,
  IconButton,
  IDropdownOption,
  Label,
  PrimaryButton,
  SelectionMode,
  Spinner,
  SpinnerSize,
  Stack,
} from '@fluentui/react';
import { RootState } from '../../redux/types';
import { useParams } from 'react-router-dom';
import { css } from '../../util/Css';
import { useEffect, useMemo } from 'react';
import { DateSelector } from '../../components/core/DateSelector';
import { useQueryString } from '../../util/hooks';
import { fetchBucketLogs, fetchBuckets, fetchBucketUpdateReasons, updateBuckets } from '../../api/markets';
import { useState } from 'react';
import { Timeslot, TimeslotAdjustmentLog, TimeslotAdjustmentReason, TimeslotAggregate } from '../../api/markets.types';
import { ServiceOffering } from '../../api/services.types';
import { Badge } from '../../components/core/Badge';
import { correctCapacityForSlot } from '../../api/timeslots';
import { useCallback } from 'react';

function sortFunc(a: ServiceOffering, b: ServiceOffering) {
  const sortingArr = ['THERAPY_CHILD', 'THERAPY_ADULT', 'TELEMED_CHILD', 'ANPS_CHILD', 'SICK_CHILD', 'TELEMED_ADULT', 'ANPS_ADULT', 'SICK_ADULT'];
  return sortingArr.indexOf(b.code) - sortingArr.indexOf(a.code);
}

export const MarketCapacity = () => {
  const { code } = useParams<any>();
  const optionKeyDepressed = useSelector((state: RootState) => state.ui.optionKeyDepressed);
  const markets = useSelector((state: RootState) => state.markets.allMarkets.data || []);
  const services = useSelector((state: RootState) => state.serviceOffering.services.data || []);
  const [date, setDate] = useQueryString('date', moment().format('YYYY-MM-DD'));
  const [supportedServices, setSupportedServices] = useState<ServiceOffering[]>([]);
  const [adjustmentReasons, setAdjustmentReasons] = useState<TimeslotAdjustmentReason[]>([]);
  const [serviceToTimeslotAggregates, setServiceToTimeslotAggregates] = useState<Record<string, TimeslotAggregate[]>>({});

  const [fetching, setFetching] = useState(true);

  // Get the adjustment reasons
  useEffect(() => {
    fetchBucketUpdateReasons().then((reasons) => setAdjustmentReasons(reasons || []));
  }, []);

  // Filter the supported services to the market
  useEffect(() => {
    if (!services || services.length === 0 || !markets || markets.length === 0 || !code) {
      return;
    }

    const market = markets.find((m) => m.code === code);
    if (market) {
      const supported: ServiceOffering[] = [];
      market.serviceOfferingMarkets.forEach((som) => {
        const service = services.find((s) => s.code === som.serviceCode);
        if (service) {
          supported.push(service);
        }
      });

      supported.sort(sortFunc);
      setSupportedServices(supported);
    }
  }, [services, markets, code]);

  // Get all the market/service/date aggregates
  useEffect(() => {
    if (supportedServices.length > 0 && code) {
      setFetching(true);
      const promises = supportedServices.map((service) => fetchBuckets(code, [service.code], date));
      Promise.allSettled(promises).then((r) => {
        const aggregates: Record<string, TimeslotAggregate[]> = {};
        supportedServices.forEach((element, index) => {
          /* @ts-ignore: next-line */
          aggregates[element.code] = r[index]['value'];
        });
        setServiceToTimeslotAggregates(aggregates);
        setFetching(false);
      });
    }
  }, [supportedServices, date, code]);

  const idToTimeslotMap: Record<string, Timeslot> = useMemo(() => {
    const result: { [key: string]: Timeslot } = {};
    Object.keys(serviceToTimeslotAggregates).forEach((key) => {
      serviceToTimeslotAggregates[key].forEach((a) => a.timeslots.forEach((t) => (result[t.id] = t)));
    });
    return result;
  }, [serviceToTimeslotAggregates]);

  const save = (adjustmentReasonId: string, timeslots: { id: string; backorder: number; status: string }[]) => {
    updateBuckets(adjustmentReasonId, timeslots).then((ok) => {
      if (ok) {
        // trigger refresh.
        setSupportedServices(supportedServices.slice());
      }
    });
  };

  return (
    <>
      <Stack horizontalAlign="start" horizontal={true}>
        <DateSelector outputFormat="YYYY-MM-DD" defaultDate={moment(date).toDate()} showNudgeButtons={true} onChange={(d) => setDate(d)} />
        {optionKeyDepressed && (
          <DefaultButton
            text="Trigger correction (all slots)"
            onClick={() => {
              Object.keys(idToTimeslotMap).forEach((id) => correctCapacityForSlot(id));
            }}
          />
        )}
      </Stack>

      {fetching && <Spinner size={SpinnerSize.large} label="Fetching capacity..." />}
      {!fetching && (
        <CapacityTable
          date={date}
          services={supportedServices}
          serviceToTimeslotAggregates={serviceToTimeslotAggregates}
          adjustmentReasons={adjustmentReasons}
          updateTimeslots={save}
        />
      )}

      {!fetching && <Logs adjustmentReasons={adjustmentReasons} idToTimeslotMap={idToTimeslotMap} market={code!} date={date} />}
    </>
  );
};

interface Props {
  date: string;
  services: ServiceOffering[];
  serviceToTimeslotAggregates: { [serviceCode: string]: TimeslotAggregate[] };
  adjustmentReasons: TimeslotAdjustmentReason[];
  updateTimeslots: (adjustmentReasonId: string, timeslots: { id: string; backorder: number; status: string }[]) => void;
}

interface State {
  reasonId: string;
  timeslotUpdates: { [timeslotId: string]: { slotBackorder: number; status: string } };
}

class CapacityTable extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      reasonId: '',
      timeslotUpdates: {},
    };
  }

  public componentDidUpdate(prevProps: Props) {
    if (prevProps.serviceToTimeslotAggregates !== this.props.serviceToTimeslotAggregates) {
      this.setState({
        reasonId: '',
        timeslotUpdates: {},
      });
    }
  }

  private buildColumns(services: ServiceOffering[]) {
    let firstAggregate: TimeslotAggregate[] = [];
    if (Object.values(this.props.serviceToTimeslotAggregates).length > 0) {
      firstAggregate = this.props.serviceToTimeslotAggregates[services[0].code];
    }

    const columns: IColumn[] = [
      {
        key: 'name',
        name: 'Name',
        fieldName: 'name',
        minWidth: 160,
        maxWidth: 180,
        onRender: (item: ServiceOffering) => {
          return (
            <div className="ms-font-l" style={{ paddingTop: 20, paddingLeft: 8 }}>
              <div>{item.shortName}</div>
              {item.name.indexOf('child') > 0 && <Badge text="child" style={{ marginTop: 8, textAlign: 'center', width: 80, background: '#ed8936' }} />}
              {item.name.indexOf('adult') > 0 && <Badge text="adult" style={{ marginTop: 8, textAlign: 'center', width: 80, background: '#48bb78' }} />}
            </div>
          );
        },
      },
    ];

    firstAggregate.forEach((element, index) => {
      columns.push({
        key: element.timeslots[0].shortTimeString,
        name: element.timeslots[0].shortTimeString,
        minWidth: 180,
        maxWidth: 180,
        onRender: (item: ServiceOffering) => {
          const serviceToTimeslotAggregates = this.props.serviceToTimeslotAggregates[item.code];
          if (!serviceToTimeslotAggregates) {
            return null;
          }
          const timeslotAggregate = serviceToTimeslotAggregates[index];
          if (!timeslotAggregate) {
            return null;
          }

          let timeslot = timeslotAggregate.timeslots[0];
          const timeslotUpdates = this.state.timeslotUpdates[timeslot.id];

          if (timeslotUpdates !== undefined) {
            timeslot = {
              ...timeslot,
              slotBackorder: timeslotUpdates.slotBackorder,
              status: timeslotUpdates.status,
            };
          }

          return (
            <Cell
              timeslot={timeslot}
              increment={() => this.incrementBackorder(timeslot)}
              decrement={() => this.decrementBackorder(timeslot)}
              toggle={() => this.toggleMarket(timeslot)}
            />
          );
        },
      });
    });

    return columns;
  }

  public render(): JSX.Element {
    const telemedServices = this.props.services.filter((s) => s.code.startsWith('TELEMED') || s.code.startsWith('THERAPY'));
    const houseCallServices = this.props.services.filter((s) => !s.code.startsWith('TELEMED') && !s.code.startsWith('THERAPY'));
    return (
      <>
        {telemedServices.length > 0 && (
          <DetailsList
            className={styles.list}
            layoutMode={DetailsListLayoutMode.fixedColumns}
            compact={true}
            selectionMode={SelectionMode.none}
            constrainMode={ConstrainMode.unconstrained}
            checkboxVisibility={CheckboxVisibility.hidden}
            items={this.props.services.filter((s) => s.code.startsWith('TELEMED') || s.code.startsWith('THERAPY'))}
            setKey="set"
            columns={this.buildColumns(telemedServices)}
          />
        )}
        {houseCallServices.length > 0 && (
          <DetailsList
            className={styles.list}
            layoutMode={DetailsListLayoutMode.fixedColumns}
            compact={true}
            selectionMode={SelectionMode.none}
            constrainMode={ConstrainMode.unconstrained}
            checkboxVisibility={CheckboxVisibility.hidden}
            items={this.props.services.filter((s) => !s.code.startsWith('TELEMED') && !s.code.startsWith('THERAPY'))}
            setKey="set"
            columns={this.buildColumns(houseCallServices)}
          />
        )}
        <Stack horizontal={true} verticalAlign="end" tokens={{ childrenGap: 10 }}>
          <Dropdown
            label="Reason for the change"
            options={this.props.adjustmentReasons.map((tar) => ({ text: tar.displayText, key: tar.id }))}
            selectedKey={this.state.reasonId}
            onChange={this.onReasonChange}
          />
          <PrimaryButton disabled={!this.canSave()} text="Save" onClick={this.onSaveClick} />
        </Stack>
      </>
    );
  }

  private incrementBackorder = (ts: Timeslot) => {
    const updates = this.initTimeslotUpdates(ts);

    updates[ts.id].slotBackorder += 1;
    this.setState({ timeslotUpdates: updates });
  };

  private decrementBackorder = (ts: Timeslot) => {
    const updates = this.initTimeslotUpdates(ts);

    updates[ts.id].slotBackorder -= 1;
    this.setState({ timeslotUpdates: updates });
  };

  private toggleMarket = (ts: Timeslot) => {
    const updates = this.initTimeslotUpdates(ts);

    if (updates[ts.id].status === 'CLOSE') {
      updates[ts.id].status = 'OPEN';
    } else {
      updates[ts.id].status = 'CLOSE';
    }
    this.setState({ timeslotUpdates: updates });
  };

  private initTimeslotUpdates = (ts: Timeslot) => {
    const updates = {
      ...this.state.timeslotUpdates,
    };

    if (!updates[ts.id]) {
      updates[ts.id] = {
        slotBackorder: ts.slotBackorder,
        status: ts.status,
      };
    }

    return updates;
  };

  private onReasonChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
    if (option) {
      this.setState({ reasonId: option.key as string });
    }
  };

  private canSave(): boolean {
    const updates = _.toArray(this.state.timeslotUpdates);
    return !!this.state.reasonId && updates.length > 0;
  }

  private onSaveClick = () => {
    const updates: { id: string; backorder: number; status: string }[] = [];
    for (const id in this.state.timeslotUpdates) {
      if (this.state.timeslotUpdates[id]) {
        const t = this.state.timeslotUpdates[id];
        updates.push({ id: id, backorder: t.slotBackorder, status: t.status });
      }
    }
    this.props.updateTimeslots(this.state.reasonId, updates);
  };
}

interface CellProps {
  timeslot: Timeslot;
  increment: () => void;
  decrement: () => void;
  toggle: () => void;
}

const Cell = ({ timeslot, increment, decrement, toggle }: CellProps) => {
  const optionKeyDepressed = useSelector((state: RootState) => state.ui.optionKeyDepressed);
  const isClosed = timeslot.status === 'CLOSE';
  const remaining = timeslot.slotTotal + timeslot.slotBackorder - timeslot.slotUsedCheck;
  const classNames = css(styles.cell, {
    [styles.closed]: isClosed,
    [styles.full]: remaining <= 0,
    [styles.open]: remaining > 0,
  });

  const correctCapacity = () => {
    correctCapacityForSlot(timeslot.id);
  };

  const renderBackorder = useCallback(() => {
    const showBackOrder = timeslot.slotBackorder !== 0 && timeslot.status !== 'CLOSE';
    const prefix = timeslot.slotBackorder > 0 ? 'Add' : 'Remove';
    const className = timeslot.slotBackorder > 0 ? styles.add : styles.remove;
    return <Badge text={`${prefix} ${timeslot.slotBackorder}`} className={className} style={{ visibility: showBackOrder ? 'visible' : 'hidden' }} />;
  }, [timeslot]);

  const renderButtons = useCallback(() => {
    const isClosed = timeslot.status === 'CLOSE';
    const isEditable = new Date().getTime() < new Date(timeslot.endTime).getTime();
    return (
      <div style={{ visibility: isEditable ? 'visible' : 'hidden' }}>
        {!isClosed && <IconButton title="Add capacity" iconProps={{ iconName: 'ChevronUpSmall' }} onClick={increment} />}
        {!isClosed && <IconButton title="Remove capacity" iconProps={{ iconName: 'ChevronDownSmall' }} onClick={decrement} />}
        <IconButton title={isClosed ? 'Open time slot' : 'Close time slot'} iconProps={{ iconName: isClosed ? 'Play' : 'Stop' }} onClick={toggle} />
      </div>
    );
  }, [timeslot, increment, decrement, toggle]);

  return (
    <>
      <div className={classNames}>
        <div style={{ width: 50 }}>
          <span style={{ fontSize: 24 }}>{isClosed ? 0 : remaining}</span>
          <span style={{ fontSize: 16 }}>/</span>
          <span style={{ fontSize: 16 }}>{isClosed ? 0 : timeslot.slotTotal}</span>
        </div>
        {renderBackorder()}
        {renderButtons()}
        {optionKeyDepressed && <IconButton style={{ position: 'absolute', top: 10, right: 10 }} iconProps={{ iconName: 'TriggerApproval' }} onClick={correctCapacity} />}
        <div className={styles.smartData}>
          {timeslot.smartCapacity !== null && (
            <span>
              Smart capacity: {timeslot.smartCapacity}
              <br />
            </span>
          )}
          {timeslot.availableDoctorMinutes !== null && <span>Available minutes: {timeslot.availableDoctorMinutes}</span>}
        </div>
      </div>
    </>
  );
};

const Logs = ({
  adjustmentReasons,
  idToTimeslotMap,
  market,
  date,
}: {
  adjustmentReasons: TimeslotAdjustmentReason[];
  idToTimeslotMap: Record<string, Timeslot>;
  market: string;
  date: string;
}) => {
  const [logs, setLogs] = useState<TimeslotAdjustmentLog[]>([]);

  useEffect(() => {
    fetchBucketLogs(market, date).then((r) => setLogs(r || []));
  }, [market, date]);

  const requestToChange = _.groupBy(_.orderBy(logs, 'createdAt', 'desc'), 'createdByRequestId');
  const items = Object.keys(requestToChange).map((key) => {
    const change = requestToChange[key];
    const first = change[0];
    const reason = adjustmentReasons.find((a) => a.id === first.adjustmentReasonId)?.displayText || first.adjustmentReasonId;
    const description = (
      <div>
        <span title={first.userAccountId}>{first.userAccountUsername || first.userAccountId}</span>
        {` made ${change.length} update${change.length === 1 ? '' : 's'} with the reason `}
        <strong>{reason}</strong>
      </div>
    );
    const comments = change.map((c, i) => {
      let timeslot = c.timeslotId;
      if (idToTimeslotMap[c.timeslotId]) {
        timeslot = idToTimeslotMap[c.timeslotId].shortTimeString + ' ' + idToTimeslotMap[c.timeslotId].serviceCode;
      }

      if (c.slotStatusOld !== c.slotStatusNew) {
        return <div key={i}>{`The ${timeslot} status was changed from ${c.slotStatusOld} to ${c.slotStatusNew}`}</div>;
      } else if (c.slotBackorderOld !== c.slotBackorderNew) {
        return <div key={i}>{`The ${timeslot} capacity backorder was changed from ${c.slotBackorderOld} to ${c.slotBackorderNew}`}</div>;
      } else {
        return <div key={i}>{`The ${timeslot} capacity was changed - unable to resolve what changed.`}</div>;
      }
    });

    return {
      activityDescription: description,
      activityIcon: <Icon iconName="Edit" />,
      comments: comments,
      timeStamp: moment(first.createdAt).format('h:mm:ss A - MM/DD/YYYY'),
    };
  });

  if (logs.length === 0) {
    return null;
  }

  return (
    <>
      <Label style={{ marginTop: 20, marginBottom: 20 }} className="ms-font-xxl">
        Logs
      </Label>
      {items.map((v, i) => (
        <ActivityItem styles={{ root: { fontSize: 16, margin: 10 } }} key={i} {...v} />
      ))}
    </>
  );
};
